import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { formatAssetDisplayName } from "../../lib/format-asset-name";

/** Show the full value on hover once the label is long enough that truncation is likely. */
const TOOLTIP_MIN_LENGTH = 24;

export interface AssetNameLabelProps {
  name: string;
  /** Shown in the tooltip instead of the name — e.g. the URL behind a numbered creative. */
  detail?: string | null;
  className?: string;
}

/** Truncated asset label with a tooltip for the full name when shortened or long. */
export function AssetNameLabel({
  name,
  detail,
  className,
}: AssetNameLabelProps) {
  const display = formatAssetDisplayName(name);
  const tooltip = detail ?? name;
  const showTooltip =
    detail != null || display !== name || name.length >= TOOLTIP_MIN_LENGTH;

  const label = (
    // `block` keeps truncation working when the label isn't a flex item (flex children are
    // blockified already, but an inline span ignores `overflow: hidden` and spills its row).
    <span
      className={cn("block min-w-0 truncate", className)}
      title={showTooltip ? undefined : name}
    >
      {display}
    </span>
  );

  if (!showTooltip) {
    return label;
  }

  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>{label}</TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-xs break-all">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
